import React, { useState, useEffect } from 'react';
import {
  InputAdornment,
  Link,
  ThemeProvider,
  Box,
  Divider,
} from '@mui/material';
import { Email, Person } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { signUp as signUpApi } from '../DataService';
import { saveQueryParamsToCookies } from '../CookieHelper';
import { theme } from '../styles/theme/theme';
import {
  AuthPageContainer,
  AuthFormCard,
  AuthFormContainer,
  AuthNavContainer,
  AuthPageTitle,
  AuthSubtitle,
  AuthErrorMessage,
  AuthTextField,
  AuthGradientButton,
} from '../styles/components';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location.search) {
      saveQueryParamsToCookies(location.search);
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUpApi({
        name: formData.name,
        email: formData.email,
      });
      navigate('/sign-up-confirm', { state: { email: formData.email } });
    } catch (err: any) {
      setError(err?.message || 'Sign up failed.');
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthPageContainer>
        <AuthFormCard elevation={6}>
          <AuthPageTitle variant="h5">
            Sign Up
          </AuthPageTitle>
          
          <AuthSubtitle variant="body2">
            Create your account
          </AuthSubtitle>

          <Box component="form" onSubmit={handleSubmit}>
            <AuthFormContainer>
              <AuthTextField
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                autoFocus
                value={formData.name}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <AuthTextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <AuthErrorMessage>
                  {error}
                </AuthErrorMessage>
              )}

              <AuthGradientButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Sign Up'}
              </AuthGradientButton>

              <Divider sx={{ my: 2, width: '100%' }}>OR</Divider>

              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <GoogleLogin
                  text="signup_with"
                  onSuccess={(credentialResponse) => {
                    if (credentialResponse.credential) {
                      navigate(`/google-callback?id_token=${credentialResponse.credential}&flow=signup`);
                    }
                  }}
                  onError={() => {
                    setError('Google sign-up failed. Please try again.');
                  }}
                />
              </Box>

              <AuthNavContainer>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/sign-in')}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  Already have an account? Sign In
                </Link>
              </AuthNavContainer>
            </AuthFormContainer>
          </Box>
        </AuthFormCard>
      </AuthPageContainer>
    </ThemeProvider>
  );
};

export default SignUp; 